import { useCallback, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import {
  AgxButton,
  AgxDatePicker,
  AgxDivider,
  AgxDropdown,
  AgxSpacer,
  AgxTextInput,
  AgxTypography,
} from '@urbanx/agx.ui-components2'
import {
  Deposit,
  ValueType,
  Condition,
  useStaffGetSaleInformation,
} from '@urbanx/agx.campaigns.client'
import { SubPagePanel } from 'components/panel/SubPagePanel'
import ConditionDate from '../components/ConditionDate'
import { EditConditionModal } from '../modals/EditConditionModal'
import { useFormConditionDueDate } from '../components/useFormConditionDueDate'
import { parseNumber } from 'utils/parseNumber'
import { useSelectedContract } from 'hooks'
import { useGetSpecialConditionsInfo } from 'hooks/useGetSpecialConditionsInfo'

const DepositValueTypeOptions = [
  { label: ValueType.Dollar, value: ValueType.Dollar },
  { label: ValueType.Percentage, value: ValueType.Percentage },
]

interface DepositPanelProps {
  id: string
  panelTitle: string
  deposit?: Deposit
  onChange: (deposit: Deposit | undefined) => void
}

export function DepositPanel({
  id,
  panelTitle,
  deposit,
  onChange,
}: DepositPanelProps) {
  const { contract, campaignId } = useSelectedContract()

  const [editDeposit, setEditDeposit] = useState(false)
  const [showEditDateModal, setShowEditDateModal] = useState(false)
  const [depositValueType, setDepositValueType] = useState<ValueType>(
    deposit?.depositValueType || ValueType.Dollar
  )
  const [depositAmount, setDepositAmount] = useState<number | undefined>(
    deposit?.amount
  )
  const [depositPercent, setDepositPercent] = useState<number | undefined>(
    deposit?.percent
  )
  const [editErrorMessage, setEditErrorMessage] = useState<string | undefined>(
    undefined
  )
  const dueDate = useFormConditionDueDate(deposit)

  const { specialConditionIdToClauseNumberMap } = useGetSpecialConditionsInfo(
    campaignId,
    contract?.contractId
  )

  const {
    data: saleInformation,
    isPending,
    isError,
  } = useStaffGetSaleInformation(
    contract?.contractId || '',
    !!contract?.contractId
  )

  useEffect(() => {
    setDepositValueType(deposit?.depositValueType || ValueType.Dollar)
    setDepositPercent(deposit?.percent)
    setDepositAmount(deposit?.amount)
  }, [deposit])

  useEffect(() => {
    if (!isPending && !isError && saleInformation) {
      if (
        depositValueType === ValueType.Percentage &&
        saleInformation.salePrice &&
        depositPercent
      ) {
        const calculatedAmount =
          (saleInformation.salePrice * depositPercent) / 100
        setDepositAmount(calculatedAmount)
      }
    }
  }, [saleInformation, depositValueType, depositPercent, isPending, isError])

  const onSaveEditAmount = useCallback(() => {
    if (editDeposit) {
      if (depositValueType === ValueType.Percentage) {
        if (depositPercent === undefined || depositPercent < 0.1) {
          setEditErrorMessage('Minimum percentage is 0.1%')
          return
        }
        setEditErrorMessage(undefined)
        onChange({
          ...deposit,
          percent: depositPercent,
          amount: undefined,
          depositValueType: ValueType.Percentage,
        })
      } else {
        if (depositAmount === undefined || depositAmount < 1) {
          setEditErrorMessage('Minimum amount is $1')
          return
        }
        setEditErrorMessage(undefined)
        onChange({
          ...deposit,
          amount: depositAmount,
          percent: undefined,
          depositValueType: ValueType.Dollar,
        })
      }
    } else {
      setEditErrorMessage(undefined)
    }
    setEditDeposit((prev) => !prev)
  }, [
    editDeposit,
    onChange,
    deposit,
    depositAmount,
    depositPercent,
    depositValueType,
  ])

  if (!deposit) return

  const dueDateValue = dueDate?.dueDate
    ? dayjs(dueDate.dueDate).format('YYYY-MM-DD')
    : ''

  const displayLabel =
    depositValueType === ValueType.Percentage && depositPercent
      ? `${panelTitle} (${depositPercent}%)`
      : panelTitle

  return (
    <SubPagePanel
      key={`deposit-panel-${id}`}
      expandable
      panelTitle={panelTitle}
      defaultExpanded
    >
      <AgxSpacer
        direction="horizontal"
        gap="spacing-md"
        fill
        padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
      >
        <ConditionDate
          id={id}
          condition={deposit}
          isDeposit
          depositAmount={depositAmount}
          specialConditionIdToClauseNumberMap={
            specialConditionIdToClauseNumberMap ?? {}
          }
        />
      </AgxSpacer>
      <AgxDivider variant="horizontal" />
      <AgxSpacer
        direction="vertical"
        gap="spacing-md"
        fill
        padding={{ top: 'spacing-3xl', bottom: 'spacing-3xl' }}
      >
        <AgxSpacer
          direction="horizontal"
          gap="spacing-md"
          fill
          contentAlignment="end"
        >
          {editDeposit && (
            <AgxDropdown
              id={`${id}-value-type`}
              options={DepositValueTypeOptions}
              selected={depositValueType}
              onChange={(value) => setDepositValueType(value as ValueType)}
              hideOptionalText
              label="Deposit Type"
            />
          )}
          {depositValueType === ValueType.Percentage && (
            <AgxTextInput
              id={`${id}-percent`}
              type="percent"
              stretch
              disabled={!editDeposit}
              hideOptionalText
              label={displayLabel}
              defaultValue={depositPercent?.toString()}
              onChange={(value) => setDepositPercent(parseNumber(value))}
            />
          )}
          {depositValueType === ValueType.Dollar && (
            <AgxTextInput
              id={`${id}-amount`}
              type="currency"
              stretch
              disabled={!editDeposit}
              hideOptionalText
              noDecimalPoints
              label={displayLabel}
              defaultValue={depositAmount?.toString()}
              onChange={(value) => setDepositAmount(parseNumber(value))}
            />
          )}
          <AgxButton
            id={`${id}-edit-amount`}
            variant="hollow"
            text={editDeposit ? 'Save' : 'Edit'}
            onClick={onSaveEditAmount}
          />
        </AgxSpacer>
        {editErrorMessage && (
          <AgxTypography id={`${id}-edit-error`} type="form_error" size="small">
            {editErrorMessage}
          </AgxTypography>
        )}
      </AgxSpacer>
      <AgxSpacer
        direction="horizontal"
        gap="spacing-md"
        fill
        padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
        contentAlignment="end"
      >
        <AgxDatePicker
          id={`${id}-due-date`}
          stretch
          disabled={true}
          optionalText=""
          label="Due Date"
          key={dueDateValue}
          value={dueDateValue}
        />
        <AgxButton
          id={`${id}-edit-due-date`}
          variant="hollow"
          text={'Edit'}
          onClick={() => {
            setShowEditDateModal(true)
          }}
        />
      </AgxSpacer>
      <AgxSpacer
        direction="horizontal"
        gap="spacing-md"
        fill
        padding={{ top: 'spacing-md', bottom: 'spacing-md' }}
        contentAlignment="end"
        contentJustification="end"
      >
        <AgxButton
          id={`${id}-delete`}
          variant="link"
          danger
          text="Delete"
          onClick={() => onChange(undefined)}
        />
      </AgxSpacer>
      {showEditDateModal && (
        <EditConditionModal
          id={id}
          modalTitle="Edit Condition"
          formCondition={deposit}
          availableConditions={[
            Condition.NdaysFromDate,
            Condition.OnSpecificDate,
            Condition.FinanceBeingSatisfied,
            Condition.BuildingAndPestBeingSatisfied,
            Condition.UponSatisfactionOfSpecialCondition,
          ]}
          openModal={setShowEditDateModal}
          onChange={(updatedDeposit) => {
            onChange({
              ...deposit,
              ...(updatedDeposit as Deposit),
              amount: depositAmount,
              depositType: deposit?.depositType,
            })
            setShowEditDateModal(false)
          }}
          depositType={deposit?.depositType}
        />
      )}
    </SubPagePanel>
  )
}
